import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Grid, Box, Typography, styled } from "@mui/material"

import i18n from "config/i18n"
import { INSTANCE, BANKS } from "config"
import { useAppDispatch } from "redux/hooks"
import { makePayment } from "redux/features/payment/paymentThunk"
import Spinner from "components/ui/Spinner"
import Button from "components/ui/Button"
import Input from "components/ui/Input"
import Title from "components/ui/ModalTitle"
import Form from "components/Payment/Pay/Form"
import BankLinkForm from "components/Payment/Pay/BankLinkForm"
import { formatMoney } from "helpers/formatters"
import { PaymentFormValues, PaymentMethodList } from "types"

type Props = {
  debtId: string
  amount: number
  paymentMethods: PaymentMethodList
  onToggle: () => void
  onFailure: (error: string) => void
}

const PAYMENT_REQUEST_DEFAULT = {
  VK_SERVICE: "1012",
  VK_VERSION: "008",
  VK_SND_ID: "",
  VK_STAMP: "",
  VK_AMOUNT: "",
  VK_CURR: "EUR",
  VK_REF: "",
  VK_MSG: "",
  VK_RETURN: "",
  VK_CANCEL: "",
  VK_DATETIME: "",
  VK_MAC: "",
  VK_ENCODING: "UTF-8",
  VK_LANG: "ENG",
}

const NewPayment = ({
  debtId,
  amount,
  paymentMethods,
  onToggle,
  onFailure,
}: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [isForm, toggleForm] = useState(false)
  const [currentAmount, setCurrentAmount] = useState(amount)
  const [formCacheAmount, setFormCacheAmount] = useState(currentAmount)

  const [paymentRequestUri, setPaymentRequestUri] = useState("")
  const [paymentRequestMethod, setPaymentRequestMethod] = useState("POST")
  const [paymentRequestParams, setPaymentRequestParams] = useState(
    PAYMENT_REQUEST_DEFAULT,
  )
  const [isSubmitting, setSubmitting] = useState(false)

  const showForm = () => {
    toggleForm(true)
  }

  const showReset = () => {
    return amount !== currentAmount
  }

  const handleCancel = () => {
    if (formCacheAmount !== currentAmount) {
      setFormCacheAmount(currentAmount)
    }
    toggleForm(false)
  }

  const handleReset = () => {
    if (showReset()) {
      setFormCacheAmount(amount)
      setCurrentAmount(amount)
    }
  }

  const handleSubmit = (values: PaymentFormValues) => {
    if (isSubmitting) {
      onFailure(i18n.t("common.request_in_progress"))
      return false
    }
    const instance = INSTANCE
    const isBanklink = BANKS.includes(values.bank_name)
    setSubmitting(true)

    const params = {
      debt_id: debtId,
      instance,
      ...values,
    }

    dispatch(makePayment(params))
      .then(({ request_uri, request_method, request_params }) => {
        if (isBanklink) {
          setPaymentRequestUri(request_uri)
          setPaymentRequestMethod(request_method)
          setPaymentRequestParams(request_params)
        } else {
          window.location.href = request_uri
        }
      })
      .catch((error) => {
        if (typeof error === "string") {
          onFailure(error) // NOTE: flash message text
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormCacheAmount(Number(event.target.value))
  }

  const handleSave = () => {
    setCurrentAmount(Number(formCacheAmount))
    toggleForm(false)
  }

  return (
    <Container>
      <Spinner open={isSubmitting} />
      <Title>{t("features.payment.form.title")}</Title>
      <Wrapper>
        {isForm ? (
          <Input
            sx={{
              input: {
                fontSize: "3.5rem",
                fontWeight: "400",
                lineHeight: "1.4375em",
                padding: "0",
                margin: "0",
                textAlign: "center",
              },
            }}
            onChange={handleChange}
            defaultValue={formCacheAmount}
            disabled={isSubmitting}
          />
        ) : (
          <Amount variant="h1" onClick={showForm}>
            {formatMoney(formCacheAmount)}
          </Amount>
        )}
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          {isForm && (
            <LinkItem>
              <Link onClick={handleCancel}>
                {t("features.payment.form.cancel")}
              </Link>
            </LinkItem>
          )}
          {isForm && (
            <LinkItem>
              <Link onClick={handleSave}>
                {t("features.payment.form.save")}
              </Link>
            </LinkItem>
          )}
          {!isForm && showReset() && (
            <LinkItem>
              <Link onClick={handleReset} disabled={isSubmitting}>
                {t("features.payment.form.reset")}
              </Link>
            </LinkItem>
          )}
          {!isForm && (
            <LinkItem>
              <Link onClick={showForm} disabled={isSubmitting}>
                {t("features.payment.form.change")}
              </Link>
            </LinkItem>
          )}
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          {paymentMethods.map(({ color, label, value }, index: number) => (
            <Form
              key={index}
              initialValues={{
                amount: currentAmount,
                bank_name: value,
              }}
              disabled={isSubmitting || isForm}
              title={t(`banks.${label}.title`)}
              color={color}
              onSubmit={handleSubmit}
            />
          ))}
          {paymentRequestUri && (
            <BankLinkForm
              uri={paymentRequestUri}
              method={paymentRequestMethod}
              params={paymentRequestParams}
            />
          )}
        </Grid>
      </Wrapper>
      <Toggle onClick={onToggle}>
        {t("features.payment.toggle.transfer")}
      </Toggle>
    </Container>
  )
}

export default NewPayment

const Container = styled(Box)(({ theme }) => ({
  marginTop: "40px",
  [theme.breakpoints.down("lg")]: {
    margin: "0",
  },
}))

const Wrapper = styled(Box)(() => ({
  margin: "11px 0 80px 0",
}))

const Amount = styled(Typography)(() => ({
  fontSize: "3.5rem",
  fontWeight: "400",
  height: "1.4375em",
  lineHeight: "1.4375em",
  margin: "0",
  padding: "0",
  textAlign: "center",
}))

const Link = styled(Button)(({ theme }) => ({
  background: "transparent",
  color: theme.palette.primary.main,
  textAlign: "center",
  marginTop: "7px",
}))

const LinkItem = styled(Grid)(() => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
}))

const Toggle = styled(Button)(({ theme }) => ({
  background: "transparent",
  color: theme.palette.primary.main,
  textAlign: "center",
}))
